/**
 * Sets up a global axios response interceptor that reports any 5xx response
 * or network failure to the backend crash tracker. Wired up once in App.js.
 *
 * Skipped for the report endpoint itself (no recursive loops if it ever 500s)
 * and for cancelled requests.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REPORT_URL = `${API}/crash/report`;

// In-memory dedupe so a 5xx burst doesn't fire 100 reports.
const seen = new Map();   // key -> last_sent_ts
const DEDUPE_MS = 60_000;

let installed = false;

export function installCrashInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      try {
        const url = error?.config?.url || '';
        // Don't recurse into ourselves
        if (url.includes('/crash/report')) return Promise.reject(error);
        // Skip cancelled
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return Promise.reject(error);

        const status = error?.response?.status;
        const isNetwork = !error?.response;
        const method = (error?.config?.method || '').toUpperCase();

        // Decide whether to log:
        //  • Network failures (no response at all)              → log
        //  • 5xx responses                                       → log
        //  • 4xx on critical write paths (uploads, orders, …)    → log (these are silent failures the admin needs to see)
        //  • Plain 401/403/404/409/422 on read paths             → skip (normal user/permission/UX flow)
        const isWritePath = (
          method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE'
        );
        // Always log on these business-critical paths even if 4xx
        const criticalPathRegex = /\/(upload|orders|cart|checkout|admin\/products|admin\/orders|admin\/submissions|admin\/carts)/i;
        const isCriticalPath = criticalPathRegex.test(url);
        const isAuthError = status === 401 || status === 403;
        let shouldLog = false;
        if (isNetwork) shouldLog = true;
        else if (status >= 500) shouldLog = true;
        else if (status >= 400 && status !== 404 && !isAuthError && (isWritePath || isCriticalPath)) {
          // 4xx on writes/critical paths = the silent admin-flow failures we
          // were missing (e.g. 413 file-too-large during product image upload).
          shouldLog = true;
        }
        if (!shouldLog) return Promise.reject(error);

        const key = `${status || 'NET'}:${url}`;
        const now = Date.now();
        if (seen.get(key) && now - seen.get(key) < DEDUPE_MS) return Promise.reject(error);
        seen.set(key, now);

        const userEmail = (() => {
          try {
            const t = localStorage.getItem('blkmrkt_token') || sessionStorage.getItem('blkmrkt_token');
            if (!t) return null;
            return JSON.parse(atob(t.split('.')[1] || ''))?.email || null;
          } catch { return null; }
        })();

        axios.post(REPORT_URL, {
          source: 'api_error',
          error_class: isNetwork ? 'NetworkError' : `HTTP${status}`,
          error_message: (
            (error?.message || '') +
            (error?.response?.data?.detail
              ? ` · ${typeof error.response.data.detail === 'string'
                  ? error.response.data.detail.slice(0, 300)
                  : JSON.stringify(error.response.data.detail).slice(0, 300)}`
              : '')
          ),
          stack: (error?.stack || '').slice(0, 4000),
          url: typeof window !== 'undefined' ? window.location.href : null,
          http_status: status || null,
          user_email: userEmail,
          extra: {
            api_path: url,
            method,
          },
        }, { timeout: 5000 }).catch(() => { /* silent */ });
      } catch { /* silent */ }
      return Promise.reject(error);
    }
  );
}
